export const PRODUCTS_BY_SKUS_PAGE_SIZE = 100;

export type ProductsBySkusPageVariables = {
  skus: string[];
  limit: number;
  offset: number;
};

type ProductsBySkusPage<TResult> = {
  products: {
    total: number;
    results: TResult[];
  };
};

export const getProductsBySkusPageLimit = (skuCount: number) =>
  Math.min(skuCount, PRODUCTS_BY_SKUS_PAGE_SIZE);

export async function fetchAllProductsBySkusPages<
  TPage extends ProductsBySkusPage<TResult>,
  TResult,
>(
  skus: string[],
  emptyPage: TPage,
  fetchPage: (variables: ProductsBySkusPageVariables) => Promise<TPage>
): Promise<TPage> {
  if (skus.length === 0) {
    return emptyPage;
  }

  // Offset pagination is only gap/duplicate-free if the underlying query uses a stable sort
  // (the products queries pin `sort: "id asc"`); otherwise a SKU could be missed across pages
  // and wrongly read as unresolved/out of stock.
  const limit = getProductsBySkusPageLimit(skus.length);
  const firstPage = await fetchPage({ skus, limit, offset: 0 });
  const totalPageCount = Math.ceil(firstPage.products.total / limit);
  const maxPageCount = Math.ceil(skus.length / limit);
  const pageCount = Math.min(totalPageCount, maxPageCount);

  if (pageCount <= 1) {
    return firstPage;
  }

  const additionalPages = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, index) =>
      fetchPage({ skus, limit, offset: (index + 1) * limit })
    )
  );

  return {
    ...firstPage,
    products: {
      ...firstPage.products,
      results: [
        ...firstPage.products.results,
        ...additionalPages.flatMap((page) => page.products.results),
      ],
    },
  };
}
